import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const DOCS = {
    privacy: {
        title: "سياسة الخصوصية",
        testid: "legal-privacy",
        body: [
            "موقع ميام – مساحة شفاء ووعي هو موقع تعريفي فقط، ولا يقوم بجمع أو تخزين أي معلومات شخصية عن الزائرات بشكل مباشر. لا توجد في الموقع حسابات مستخدمين، نماذج تسجيل، أو قواعد بيانات للزبائن.",
            "عند الضغط على أزرار الحجز أو التواصل، يتم نقلك مباشرة إلى خدمات خارجية: نظام حجز المواعيد دُوري (store.dorie.io)، تطبيق واتساب، خرائط جوجل وإنستغرام. أي معلومات تُدخلينها هناك تخضع لسياسات الخصوصية الخاصة بتلك الخدمات، ولا تمر عبر هذا الموقع ولا تُحفظ فيه.",
            "الرسائل التي ترسلينها عبر واتساب تصل إلى مياسة وتُستخدم فقط للرد على استفسارك وتنسيق المواعيد. لن تتم مشاركة معلوماتك مع أي طرف ثالث لأغراض تسويقية.",
            "لأي سؤال حول الخصوصية، يمكنك التواصل عبر واتساب أو هاتفياً: 052-264-7497.",
            "تم آخر تحديث لهذه السياسة في عام 2026.",
        ],
    },
    terms: {
        title: "شروط الاستخدام",
        testid: "legal-terms",
        body: [
            "موقع ميام – مساحة شفاء ووعي هو موقع تعريفي يعرض خدمات العلاجات الطبيعية والمساج في يركا. المعلومات في الموقع معدّة للاطلاع العام فقط.",
            "الخدمات المقدّمة تهدف إلى دعم الراحة والعافية، ولا تُعد بديلاً عن التشخيص أو الاستشارة الطبية عند الحاجة. لا يقدّم الموقع أي وعود طبية أو ادعاءات شفاء.",
            "حجز المواعيد يتم عبر نظام دُوري الخارجي، وتخضع عمليات الحجز والدفع والإلغاء لشروط الاستخدام وسياسة الإلغاء الخاصة بنظام دُوري. الأسعار المعروضة في الموقع قابلة للتغيير، والسعر الملزم هو الذي يظهر في نظام الحجز أو يتم تأكيده مباشرة مع مياسة.",
            "جميع محتويات الموقع، بما فيها النصوص والتصميم والصور وشعار ميام وبطاقات «أي زهرة أنتِ؟»، هي ملك لميام ولا يجوز نسخها أو استخدامها دون إذن مسبق.",
            "تم آخر تحديث لهذه الشروط في عام 2026.",
        ],
    },
    accessibility: {
        title: "بيان إمكانية الوصول",
        testid: "legal-accessibility",
        body: [
            "نحرص في ميام على أن يكون الموقع متاحاً ومريحاً لجميع الزائرات، بما في ذلك النساء ذوات الإعاقة، ونعمل على ملاءمته لإرشادات إمكانية الوصول WCAG 2.1 بمستوى AA قدر الإمكان.",
            "من بين الملاءمات الموجودة في الموقع: دعم كامل للغة العربية ولاتجاه القراءة من اليمين إلى اليسار، عناوين وهيكلة دلالية واضحة، نصوص بديلة للصور، تباين ألوان مريح، أزرار كبيرة وسهلة اللمس على الهاتف، وإمكانية تصفح كاملة من شاشات صغيرة.",
            "إذا واجهتِ أي صعوبة في استخدام الموقع أو كان لديك اقتراح لتحسين إمكانية الوصول، يسعدنا أن تتواصلي معنا عبر واتساب أو هاتفياً: 052-264-7497، وسنبذل قصارى جهدنا للمساعدة والتحسين.",
            "تم آخر تحديث لهذا البيان في عام 2026.",
        ],
    },
};

export const LegalModal = ({ doc, onClose }) => {
    const content = doc ? DOCS[doc] : null;

    useEffect(() => {
        if (!doc) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [doc, onClose]);

    return (
        <AnimatePresence>
            {content && (
                <motion.div
                    data-testid="legal-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={onClose}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-miam-dark/60 p-4 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    aria-label={content.title}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 32, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.97 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-miam-cream p-7 soft-shadow lg:p-10"
                    >
                        <div className="mb-6 flex items-center justify-between gap-4">
                            <h2 className="font-heading text-2xl font-bold text-miam-dark">
                                {content.title}
                            </h2>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="legal-modal-close"
                                aria-label="إغلاق"
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-miam-dark transition-colors duration-300 hover:bg-miam-rose hover:text-white"
                            >
                                <X className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="space-y-4" data-testid={content.testid}>
                            {content.body.map((p, i) => (
                                <p key={i} className="text-sm lg:text-base leading-loose text-miam-muted">
                                    {p}
                                </p>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
